import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, ShoppingBag, Package, BarChart3, Share2, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function MarketCanada() {
  const [showShareMenu, setShowShareMenu] = useState(false);

  const generatePDF = async () => {
    const content = document.getElementById('market-report-content');
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = 297;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('Canada-Market-Report-Motif Unbound.pdf');
  };

  const shareReport = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Check out this Canada Market Report from Motif Unbound');
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <div id="market-report-content" className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          Canada Market Overview
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          A sophisticated, digitally-savvy market with strong e-commerce adoption and growing demand for premium products.
        </p>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] text-white p-6 rounded-xl">
            <DollarSign size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$736B CAD</div>
            <div className="text-sm opacity-90">Total Retail Sales 2025</div>
          </div>
          <div className="bg-gradient-to-br from-[#FFD965] to-[#f5cc4d] text-[#000000] p-6 rounded-xl">
            <TrendingUp size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">+4.1%</div>
            <div className="text-sm opacity-90">YoY Growth Rate</div>
          </div>
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#000000] text-white p-6 rounded-xl">
            <ShoppingBag size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$168B CAD</div>
            <div className="text-sm opacity-90">E-commerce Sales 2025</div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">2025 Retail Performance Highlights</h2>
          <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Digital Commerce Leadership</h3>
                  <p className="text-gray-700 mb-2">Canada has one of the highest e-commerce penetration rates globally at 22.8% of total retail in 2025.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>87% of Canadians shop online regularly</li>
                    <li>Cross-border shopping from US retailers remains strong</li>
                    <li>Mobile shopping accounts for 62% of online transactions</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Consumer Preferences</h3>
                  <p className="text-gray-700 mb-2">Canadian consumers prioritize quality, sustainability, and support for local/domestic brands.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>78% prefer Canadian-made products when available</li>
                    <li>Sustainability is a key purchase driver for 71% of consumers</li>
                    <li>Premium product segments growing faster than mass market</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">USMCA Opportunities</h3>
                  <p className="text-gray-700 mb-2">The trade agreement creates significant opportunities for nearshoring and tariff-free trade.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Zero tariffs on qualifying products under USMCA</li>
                    <li>Growing manufacturing capabilities in key sectors</li>
                    <li>Strategic gateway to North American market</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Key Retail Categories in 2025</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Food & Beverage</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$156B CAD</div>
              <p className="text-gray-700 text-sm mb-3">Premium and specialty foods driving growth</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Organic and natural products</li>
                <li>• Specialty and gourmet foods</li>
                <li>• Plant-based alternatives</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Health & Personal Care</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$68B CAD</div>
              <p className="text-gray-700 text-sm mb-3">Wellness focus accelerating category growth</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Natural health products</li>
                <li>• Personal care and beauty</li>
                <li>• Fitness and wellness</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Home & Furniture</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$52B CAD</div>
              <p className="text-gray-700 text-sm mb-3">Strong demand for home improvement</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Sustainable furnishings</li>
                <li>• Smart home technology</li>
                <li>• Outdoor living spaces</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Apparel & Accessories</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$47B CAD</div>
              <p className="text-gray-700 text-sm mb-3">Performance and outdoor wear thriving</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Technical outerwear</li>
                <li>• Athletic apparel</li>
                <li>• Sustainable fashion</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Regional Market Dynamics</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Ontario (38% of national retail)</h3>
              <p className="text-gray-700 text-sm">Largest market with Toronto as the economic hub. Strong multicultural consumer base and high disposable incomes.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Quebec (23% of national retail)</h3>
              <p className="text-gray-700 text-sm">Unique French-language market with distinct cultural preferences. Strong local brand loyalty and bilingual requirements.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">British Columbia (14% of national retail)</h3>
              <p className="text-gray-700 text-sm">Affluent, health-conscious consumers. Strong demand for premium, sustainable, and Asian-influenced products.</p>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#38A3A5] to-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Expand to Canada with Confidence</h2>
          <p className="mb-6">Leverage our expertise in Canadian market entry, bilingual marketing, and USMCA compliance to build a successful presence in Canada.</p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
              className="bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              Schedule a Consult
            </a>
            <button 
              onClick={generatePDF}
              className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              <Download size={20} />
              Download Market Report
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              >
                <Share2 size={20} />
                Share Report
              </button>
              {showShareMenu && (
                <div className="absolute top-full mt-2 bg-white rounded-lg shadow-xl p-3 min-w-[200px] z-10">
                  <button
                    onClick={() => shareReport('whatsapp')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    onClick={() => shareReport('linkedin')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    💼 LinkedIn
                  </button>
                  <button
                    onClick={() => shareReport('twitter')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    🐦 Twitter
                  </button>
                  <button
                    onClick={() => shareReport('facebook')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    👥 Facebook
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default MarketCanada;
